import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Loader2, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { seedCustomerFields } from '@/lib/phone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchCombobox } from '@/components/SearchCombobox';
import type { Customer, Supplier } from '@billflow/shared';
import type { CountertopJobDetailResponse, CountertopJobResponse } from '@/lib/api-types';

// ─── Constants ─────────────────────────────────────────────────────────────────

const ACCESS_OPTIONS = ['Elevator', 'Stairs', 'Basement'] as const;
const AREA_OPTIONS = ['Countertop', 'Island', 'Waterfall', 'Backsplash', 'Fireplace'] as const;

type SinkType = 'single' | 'double';
type SinkPresent = '' | 'yes' | 'no';
type EdgeValue = '' | 'double' | 'miter';

// ─── CSV helpers ───────────────────────────────────────────────────────────────
// Multi-select intake fields persist as a comma-separated string on the server.

/** Parse a stored CSV string into the subset of `options` present (case-insensitive). */
function parseCsvSet(csv: string | null | undefined, options: readonly string[]): Set<string> {
  const tokens = (csv ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const set = new Set<string>();
  for (const opt of options) {
    if (tokens.includes(opt.toLowerCase())) set.add(opt);
  }
  return set;
}

/** Serialize the checked options back into a CSV string (or undefined when empty). */
function serializeCsvSet(set: Set<string>, options: readonly string[]): string | undefined {
  const picked = options.filter((o) => set.has(o));
  return picked.length > 0 ? picked.join(',') : undefined;
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CustomerSearchResponse {
  data: { customers: Customer[] };
}

interface SuppliersResponse {
  data: { suppliers: Supplier[] };
}

interface CreateCustomerResponse {
  data: { customer: Customer };
}

// ─── Customer Create Modal ───────────────────────────────────────────────────────

interface CustomerCreateModalProps {
  initialQuery: string;
  onCreated: (customer: Customer) => void;
  onClose: () => void;
}

function CustomerCreateModal({ initialQuery, onCreated, onClose }: CustomerCreateModalProps) {
  // The combobox searches name/code/phone, so the typed query isn't necessarily a
  // name — a phone-looking query seeds Phone (leaving Name for the user to fill).
  const seed = seedCustomerFields(initialQuery);
  const [name, setName] = useState(seed.name);
  const [phone, setPhone] = useState(seed.phone);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<CreateCustomerResponse>('/api/customers', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
        }),
      });
      const created = res.data.customer;
      toast.success(`Customer "${created.name}" created`);
      onCreated(created);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">New Customer</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ct-cust-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="ct-cust-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Customer name"
              autoFocus={seed.phone !== ''}
              disabled={loading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-cust-phone">Phone</Label>
            <Input
              id="ct-cust-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 905-555-0100"
              autoFocus={seed.phone === ''}
              disabled={loading}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-cust-email">Email</Label>
            <Input
              id="ct-cust-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. name@example.com"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Customer Combobox (with inline create) ──────────────────────────────────────

interface CustomerComboboxProps {
  value: number | null;
  displayName: string;
  onChange: (customer: Customer | null, rawText: string) => void;
  disabled?: boolean;
}

function CustomerCombobox({ value, displayName, onChange, disabled }: CustomerComboboxProps) {
  const [query, setQuery] = useState(displayName);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setQuery(displayName);
  }, [displayName]);

  const shouldSearch = query.length >= 1 && value === null;

  const { data, isFetching } = useQuery({
    queryKey: ['customer-search', query],
    queryFn: () => {
      const params = new URLSearchParams({ q: query, status: 'active' });
      return apiFetch<CustomerSearchResponse>(`/api/customers?${params}`);
    },
    enabled: shouldSearch,
    staleTime: 10_000,
  });

  const customers = shouldSearch ? (data?.data.customers ?? []) : [];

  function handleSelect(c: Customer) {
    onChange(c, c.name);
    setQuery(c.name);
  }

  function handleQueryChange(q: string) {
    setQuery(q);
    if (value !== null) onChange(null, q);
  }

  function handleCustomerCreated(c: Customer) {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    queryClient.invalidateQueries({ queryKey: ['customer-search'] });
    handleSelect(c);
  }

  const showCreate = !isFetching && query.trim().length >= 1 && value === null;

  return (
    <>
      <SearchCombobox<Customer>
        value={value}
        query={query}
        onQueryChange={handleQueryChange}
        results={customers}
        getKey={(c) => c.id}
        renderResult={(c) => (
          <>
            <span className="font-medium">{c.name}</span>
            {c.code && <span className="text-muted-foreground font-mono">{c.code}</span>}
            {c.phone && <span className="text-muted-foreground ml-auto">{c.phone}</span>}
          </>
        )}
        onSelect={handleSelect}
        placeholder="Search customers…"
        disabled={disabled}
        loading={isFetching && shouldSearch}
        promptMessage="Type to search customers"
        emptyMessage={value === null ? 'No customers found' : undefined}
        footer={(q) =>
          showCreate ? (
            <div className="border-t">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCreateModal(true);
                }}
              >
                <Plus className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                <span className="text-muted-foreground">Create customer</span>
                <span className="font-medium text-foreground truncate">
                  &ldquo;{q.trim()}&rdquo;
                </span>
              </button>
            </div>
          ) : null
        }
      />

      {showCreateModal &&
        createPortal(
          <CustomerCreateModal
            initialQuery={query.trim()}
            onCreated={(c) => {
              handleCustomerCreated(c);
              setShowCreateModal(false);
            }}
            onClose={() => setShowCreateModal(false)}
          />,
          document.body,
        )}
    </>
  );
}

// ─── Supplier Combobox (select-only) ─────────────────────────────────────────────

interface SupplierComboboxProps {
  value: number | null;
  displayName: string;
  onChange: (id: number | null, name: string) => void;
  disabled?: boolean;
}

function SupplierCombobox({ value, displayName, onChange, disabled }: SupplierComboboxProps) {
  const [query, setQuery] = useState(displayName);

  useEffect(() => {
    setQuery(displayName);
  }, [displayName]);

  const { data, isFetching } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: () => apiFetch<SuppliersResponse>('/api/suppliers?status=active'),
    staleTime: 30_000,
  });

  const allSuppliers = data?.data.suppliers ?? [];
  const shouldFilter = value === null;
  const suppliers = !shouldFilter
    ? []
    : query.trim()
      ? allSuppliers.filter(
          (s) =>
            s.name.toLowerCase().includes(query.toLowerCase()) ||
            (s.code && s.code.toLowerCase().includes(query.toLowerCase())),
        )
      : allSuppliers;

  function handleSelect(s: Supplier) {
    onChange(s.id, s.name);
    setQuery(s.name);
  }

  function handleQueryChange(q: string) {
    setQuery(q);
    if (value !== null) onChange(null, q);
  }

  return (
    <SearchCombobox<Supplier>
      value={value}
      query={query}
      onQueryChange={handleQueryChange}
      results={suppliers}
      getKey={(s) => s.id}
      renderResult={(s) => (
        <>
          <span className="font-medium flex-1">{s.name}</span>
          {s.code && <span className="text-xs text-muted-foreground font-mono">{s.code}</span>}
        </>
      )}
      onSelect={handleSelect}
      placeholder="Search install suppliers…"
      disabled={disabled}
      loading={isFetching && shouldFilter}
      promptMessage="Type to filter suppliers"
      emptyMessage={value === null ? 'No active suppliers found' : undefined}
    />
  );
}

// ─── Yes/No toggle ───────────────────────────────────────────────────────────────

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center rounded-md overflow-hidden border border-input h-9 w-28">
      {[
        { label: 'No', v: false },
        { label: 'Yes', v: true },
      ].map((opt, i) => (
        <button
          key={opt.label}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.v)}
          className={`flex-1 text-xs font-medium transition-colors h-full ${i === 1 ? 'border-l border-input' : ''} ${
            value === opt.v
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Checkbox group ──────────────────────────────────────────────────────────────

function CheckboxGroup({
  options,
  selected,
  onToggle,
  disabled,
}: {
  options: readonly string[];
  selected: Set<string>;
  onToggle: (opt: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const checked = selected.has(opt);
        return (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(opt)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
              checked
                ? 'border-primary bg-primary/10 text-foreground font-medium'
                : 'border-input bg-background text-muted-foreground hover:text-foreground'
            }`}
          >
            <span
              className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${
                checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
              }`}
            >
              {checked && (
                <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
                  <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="1.6" />
                </svg>
              )}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── Radio group ─────────────────────────────────────────────────────────────────

function RadioGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center rounded-md border border-input overflow-hidden h-9">
      {options.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={`px-4 text-sm font-medium transition-colors h-full ${i > 0 ? 'border-l border-input' : ''} ${
            value === opt.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────────

export default function CountertopJobFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Form state ──
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [supplierName, setSupplierName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  // Tracks whether the user has hand-edited the site address; once edited we stop
  // auto-overwriting it from a newly-picked customer.
  const siteAddressTouched = useRef(false);
  const [access, setAccess] = useState<Set<string>>(new Set());
  const [materialDetail, setMaterialDetail] = useState('');
  const [approxSqft, setApproxSqft] = useState('');
  const [area, setArea] = useState<Set<string>>(new Set());
  const [removal, setRemoval] = useState(false);
  const [garbageDisposal, setGarbageDisposal] = useState(false);
  const [cooktopCutout, setCooktopCutout] = useState(false);
  const [sinkPresent, setSinkPresent] = useState<SinkPresent>('');
  const [sinkType, setSinkType] = useState<SinkType>('single');
  const [sinkDimensions, setSinkDimensions] = useState('');
  const [edge, setEdge] = useState<EdgeValue>('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // ── Load job for edit mode ──
  const { data: editData, isLoading: editLoading } = useQuery({
    queryKey: ['countertop-job', id],
    queryFn: () => apiFetch<CountertopJobDetailResponse>(`/api/countertop-jobs/${id}`),
    enabled: isEdit,
    staleTime: Infinity,
  });

  // Guard: a cancelled/installed job can't be edited (matches the server's 409).
  useEffect(() => {
    if (!isEdit || !editData) return;
    const st = editData.data.countertop_job.status;
    if (st === 'cancelled' || st === 'installed') {
      toast.error('A cancelled or installed job can no longer be edited.');
      navigate(`/countertop-jobs/${id}`, { replace: true });
    }
  }, [isEdit, editData, id, navigate]);

  // One-shot prefill from the loaded job.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!editData || prefilled.current) return;
    prefilled.current = true;
    const j = editData.data.countertop_job;
    setCustomerId(j.customer_id);
    setCustomerName(j.customer_name);
    setSupplierId(j.supplier_id);
    setSupplierName(j.supplier_name ?? '');
    setSiteAddress(j.site_address ?? '');
    if (j.site_address) siteAddressTouched.current = true;
    setAccess(parseCsvSet(j.access_detail, ACCESS_OPTIONS));
    setMaterialDetail(j.material_detail ?? '');
    setApproxSqft(j.approx_sqft ?? '');
    setArea(parseCsvSet(j.measurement_area, AREA_OPTIONS));
    setRemoval(!!j.removal);
    setGarbageDisposal(!!j.garbage_disposal);
    setCooktopCutout(!!j.cooktop_cutout);
    if (j.kitchen_sink === 'single' || j.kitchen_sink === 'double') {
      setSinkPresent('yes');
      setSinkType(j.kitchen_sink);
    } else if (j.kitchen_sink === 'none') {
      setSinkPresent('no');
      setSinkDimensions(j.sink_dimensions ?? '');
    } else {
      setSinkPresent('');
    }
    setEdge((j.edge ?? '') as EdgeValue);
    setNotes(j.notes ?? '');
  }, [editData]);

  function toggleSetMember(setter: (fn: (prev: Set<string>) => Set<string>) => void, opt: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt);
      else next.add(opt);
      return next;
    });
  }

  function handleCustomerChange(customer: Customer | null, rawText: string) {
    setCustomerId(customer?.id ?? null);
    setCustomerName(customer?.name ?? rawText);
    // Prefill the site address from the customer's address only if the user hasn't
    // typed their own yet (most jobs install at the customer's address).
    if (customer && !siteAddressTouched.current) {
      setSiteAddress(customer.address ?? '');
    }
  }

  // ── Validation ──
  function validate(): boolean {
    if (!customerId) {
      toast.error('Select a customer');
      return false;
    }
    return true;
  }

  // ── Build body ──
  function buildBody(): Record<string, unknown> {
    // Kitchen sink: Yes → single/double (no spec); No → 'none' + required spec;
    // unanswered → omit both so nothing is blocked or overwritten on edit.
    let kitchenSink: string | undefined;
    let sinkSpec: string | undefined;
    if (sinkPresent === 'yes') {
      kitchenSink = sinkType;
      sinkSpec = undefined;
    } else if (sinkPresent === 'no') {
      kitchenSink = 'none';
      sinkSpec = sinkDimensions.trim() || undefined;
    }
    return {
      customer_id: customerId,
      supplier_id: supplierId ?? null,
      site_address: siteAddress.trim() || undefined,
      access_detail: serializeCsvSet(access, ACCESS_OPTIONS),
      material_detail: materialDetail.trim() || undefined,
      approx_sqft: approxSqft.trim() || undefined,
      measurement_area: serializeCsvSet(area, AREA_OPTIONS),
      removal,
      garbage_disposal: garbageDisposal,
      cooktop_cutout: cooktopCutout,
      kitchen_sink: kitchenSink,
      sink_dimensions: sinkSpec,
      edge: edge || undefined,
      notes: notes.trim() || undefined,
    };
  }

  // ── Submit ──
  async function handleSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (isEdit) {
        await apiFetch<CountertopJobResponse>(`/api/countertop-jobs/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(buildBody()),
        });
        toast.success('Job updated');
        queryClient.invalidateQueries({ queryKey: ['countertop-jobs'] });
        queryClient.invalidateQueries({ queryKey: ['countertop-job', id] });
        navigate(`/countertop-jobs/${id}`);
      } else {
        const res = await apiFetch<CountertopJobResponse>('/api/countertop-jobs', {
          method: 'POST',
          body: JSON.stringify(buildBody()),
        });
        toast.success('Countertop job created');
        queryClient.invalidateQueries({ queryKey: ['countertop-jobs'] });
        navigate(`/countertop-jobs/${res.data.countertop_job.id}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  if (isEdit && editLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const pageTitle = isEdit
    ? `Edit ${editData?.data.countertop_job.number ?? 'Countertop Job'}`
    : 'New Countertop Job';
  const backTo = isEdit ? `/countertop-jobs/${id}` : '/countertop-jobs';

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
      {/* ── Top bar ── */}
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {isEdit ? 'Back to job' : 'Back to Countertop Jobs'}
        </Link>
      </div>

      <h1 className="text-2xl font-bold tracking-tight">{pageTitle}</h1>

      {/* ── Card: Parties & site ── */}
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Customer &amp; Site
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>
              Customer <span className="text-destructive">*</span>
            </Label>
            <CustomerCombobox
              value={customerId}
              displayName={customerName}
              onChange={handleCustomerChange}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Install supplier</Label>
            <SupplierCombobox
              value={supplierId}
              displayName={supplierName}
              onChange={(sid, sname) => {
                setSupplierId(sid);
                setSupplierName(sname);
              }}
              disabled={submitting}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ct-site-address">Site address</Label>
          <textarea
            id="ct-site-address"
            value={siteAddress}
            onChange={(e) => {
              siteAddressTouched.current = true;
              setSiteAddress(e.target.value);
            }}
            rows={2}
            disabled={submitting}
            placeholder="Where the countertop will be installed…"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Access</Label>
          <CheckboxGroup
            options={ACCESS_OPTIONS}
            selected={access}
            onToggle={(opt) => toggleSetMember(setAccess, opt)}
            disabled={submitting}
          />
        </div>
      </div>

      {/* ── Card: Material & scope ── */}
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Material &amp; Scope
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ct-material">Material / stone name</Label>
            <Input
              id="ct-material"
              value={materialDetail}
              onChange={(e) => setMaterialDetail(e.target.value)}
              placeholder="e.g. Calacatta Gold quartz"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ct-approx-sqft">Approx sq.ft.</Label>
            <Input
              id="ct-approx-sqft"
              value={approxSqft}
              onChange={(e) => setApproxSqft(e.target.value)}
              placeholder="e.g. 10×12 ≈ 120 sq ft"
              disabled={submitting}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Measurement area</Label>
          <CheckboxGroup
            options={AREA_OPTIONS}
            selected={area}
            onToggle={(opt) => toggleSetMember(setArea, opt)}
            disabled={submitting}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          <div className="space-y-1.5">
            <Label>Removal of old top</Label>
            <YesNoToggle value={removal} onChange={setRemoval} disabled={submitting} />
          </div>
          <div className="space-y-1.5">
            <Label>Garbage disposal</Label>
            <YesNoToggle
              value={garbageDisposal}
              onChange={setGarbageDisposal}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Cooktop cutout</Label>
            <YesNoToggle value={cooktopCutout} onChange={setCooktopCutout} disabled={submitting} />
          </div>
        </div>
      </div>

      {/* ── Card: Sink & edge ── */}
      <div className="rounded-lg border bg-card p-4 space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Sink &amp; Edge
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Kitchen sink</Label>
            <RadioGroup<SinkPresent>
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
              value={sinkPresent}
              onChange={(v) => setSinkPresent(v)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Edge</Label>
            <RadioGroup<EdgeValue>
              options={[
                { label: 'Double', value: 'double' },
                { label: 'Miter', value: 'miter' },
              ]}
              value={edge}
              onChange={(v) => setEdge(v)}
              disabled={submitting}
            />
          </div>
        </div>
        {sinkPresent === 'yes' && (
          <div className="space-y-1.5">
            <Label>Sink type</Label>
            <RadioGroup<SinkType>
              options={[
                { label: 'Single', value: 'single' },
                { label: 'Double', value: 'double' },
              ]}
              value={sinkType}
              onChange={(v) => setSinkType(v)}
              disabled={submitting}
            />
          </div>
        )}
        {sinkPresent === 'no' && (
          <div className="space-y-1.5">
            <Label htmlFor="ct-sink-spec">Sink specification</Label>
            <Input
              id="ct-sink-spec"
              value={sinkDimensions}
              onChange={(e) => setSinkDimensions(e.target.value)}
              placeholder='e.g. 30" x 18" x 9" undermount'
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">Needed before scheduling measurement.</p>
          </div>
        )}
      </div>

      {/* ── Card: Notes ── */}
      <div className="rounded-lg border bg-card p-4 space-y-2">
        <Label
          htmlFor="ct-notes"
          className="text-sm font-semibold text-muted-foreground uppercase tracking-wide"
        >
          Notes
        </Label>
        <textarea
          id="ct-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={submitting}
          placeholder="Anything else worth recording for this job…"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
        />
      </div>

      {/* ── Actions ── */}
      <div className="flex items-center justify-end gap-2">
        <Link to={backTo}>
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            disabled={submitting}
          >
            Cancel
          </Button>
        </Link>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : isEdit ? (
            'Save Changes'
          ) : (
            'Create Job'
          )}
        </Button>
      </div>
    </div>
  );
}
